import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button } from "@/components/ui/PageHeader";
import {
  getCounts,
  getFollowers,
  getFollowing,
  getRequests,
  getBlocked,
  followUser,
  unfollowUser,
  acceptRequest,
  rejectRequest,
  blockUser,
  unblockUser,
  type ConnectionCounts,
  type ConnectionUser,
} from "@/api/connections";
import clsx from "clsx";

type Tab = "followers" | "following" | "requests" | "blocked";

// Every tab has its count. `blocked` counts only the members you blocked:
// a block is never counted on the side of the member blocked.
const TAB_COUNT: Record<Tab, (c: ConnectionCounts) => number> = {
  followers: (c) => c.followers,
  following: (c) => c.following,
  requests: (c) => c.pendingRequests,
  blocked: (c) => c.blocked,
};

const TAB_LABEL: Record<Tab, string> = {
  followers: "Followers",
  following: "Following",
  requests: "Requests",
  blocked: "Blocked",
};

const TAB_EMPTY: Record<Tab, string> = {
  followers: "Nobody follows you yet.",
  following: "You aren't following anyone yet.",
  requests: "No pending follow requests.",
  blocked: "You haven't blocked anyone.",
};

const TAB_FETCHER: Record<Tab, () => ReturnType<typeof getFollowers>> = {
  followers: getFollowers,
  following: getFollowing,
  requests: getRequests,
  blocked: getBlocked,
};

export default function ConnectionsLab() {
  const [tab, setTab] = useState<Tab>("followers");
  const qc = useQueryClient();

  const { data: counts } = useQuery({
    queryKey: ["connection-counts"],
    queryFn: getCounts,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["connections", tab],
    queryFn: () => TAB_FETCHER[tab](),
  });

  function refresh() {
    qc.invalidateQueries({ queryKey: ["connections"] });
    qc.invalidateQueries({ queryKey: ["connection-counts"] });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Connections"
        subtitle="Follow graph — your followers, your following."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Connections" },
        ]}
      />

      <div className="flex gap-1 border-b border-gray-200">
        {(Object.keys(TAB_LABEL) as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={clsx(
              "px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
              tab === t
                ? "border-primary-600 text-primary-700"
                : "border-transparent text-gray-500 hover:text-gray-700",
            )}
          >
            {TAB_LABEL[t]}
            {counts && (
              <span className="ml-2 text-xs text-gray-400">
                {TAB_COUNT[t](counts.data)}
              </span>
            )}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : !data || data.data.length === 0 ? (
        <p className="text-sm text-gray-500">{TAB_EMPTY[tab]}</p>
      ) : (
        <ul className="space-y-2">
          {data.data.map((u) => (
            <UserRow key={u.id} u={u} tab={tab} onChanged={refresh} />
          ))}
        </ul>
      )}
    </div>
  );
}

function UserRow({
  u,
  tab,
  onChanged,
}: {
  u: ConnectionUser;
  tab: Tab;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center gap-3">
      {u.avatarUrl ? (
        <img src={u.avatarUrl} alt="" className="w-10 h-10 rounded-full" />
      ) : (
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-100 to-accent-100 flex items-center justify-center text-sm font-semibold text-primary-700">
          {u.name.charAt(0).toUpperCase()}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="font-medium text-gray-900">{u.name}</div>
        <div className="text-xs text-gray-500">
          {u.jobTitle && `${u.jobTitle}`}
          {u.state &&
            `${u.jobTitle ? " · " : ""}${u.city ? `${u.city}, ` : ""}${u.state}`}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {tab === "followers" && (
          <Button
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={() => act(() => followUser(u.id))}
          >
            Follow back
          </Button>
        )}
        {tab === "following" && (
          <Button
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={() => act(() => unfollowUser(u.id))}
          >
            Unfollow
          </Button>
        )}
        {tab === "requests" && (
          <>
            <Button size="sm" disabled={busy} onClick={() => act(() => acceptRequest(u.id))}>
              Accept
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => act(() => rejectRequest(u.id))}
            >
              Reject
            </Button>
          </>
        )}
        {tab === "blocked" ? (
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => act(() => unblockUser(u.id))}>
            Unblock
          </Button>
        ) : (
          <Button
            size="sm"
            variant="danger"
            disabled={busy}
            onClick={() => {
              if (
                window.confirm(
                  "Block this user? You'll be hidden from each other in discovery.",
                )
              ) {
                act(() => blockUser(u.id));
              }
            }}
          >
            Block
          </Button>
        )}
      </div>
    </li>
  );
}
